/**
 * The Connect tab's Routing cards — what happens to a lead once it is scored.
 *
 * Visual Reference: `design/screens/editor-connect.html`, the **Routing**
 * section and the **Add an action** chooser. Its own caption is the spec:
 * "one routing card per band. Every action row names which system it belongs
 * to: a notification tells a person, a dispatch sends the record somewhere, and
 * the cadence chip belongs to the action rather than the recipient."
 *
 * PER BAND, NEVER A TOGGLE. RULE-030: "the model is `when band = X → do actions
 * [A, B, C]`, not a global 'notify on submit' toggle." So there is no switch on
 * this screen — there are cards, and a card is a band.
 *
 * EVERY EDIT GOES THROUGH `edit()`. Routing lives in `funnel.routing[]`, which
 * DECISION-007 makes the source of truth — `notification_actions` is a
 * projection publish rebuilds, and "never edit it directly". Writing through
 * the store is what gives this tab undo, autosave and the save-state indicator
 * without any of them being written here, the same way the Qualify tab gets
 * them.
 *
 * NO `window.prompt`, ANYWHERE. DECISION-081 settled that for saved views:
 * a browser dialog "wears the browser's chrome, blocks the page, and would HANG
 * a Playwright test rather than fail one". The action editor is therefore an
 * inline panel inside the row it edits.
 *
 * Story 9.1.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ACTION_CATALOGUE,
  ACTION_GROUPS,
  CADENCE_OPTIONS,
  DEFAULT_CADENCE_AT,
  DEFAULT_CADENCE_DAY,
  SYSTEM_LABEL,
  WEEKDAYS,
  cadenceLabel,
  describeAction,
  kindsInGroup,
  withCadence,
  type ActionKind,
  type Band,
  type BandTone,
  type Cadence,
  type RoutingAction,
} from '@leadgible/schema'
import { apiGet } from '../lib/api'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Pill } from '../ui/Pill'
import { newActionId } from './blocks'
import { edit, useEditor } from './store'
import { addActionTo } from './vocabulary'

/* ── the export's own copy, verbatim ────────────────────────────────────── */

/** RULE-031, said on the screen rather than only in the code. */
export const ROUTING_ASIDE =
  'Telling someone and sending it somewhere are separate actions on the same trigger'

/**
 * The empty copy, from the mockup's empty state — "The copy reassures before it
 * invites", in its own caption's words. RULE-060 is why it can promise this:
 * nothing about routing touches capture, so an unconfigured funnel loses
 * nothing.
 */
export const EMPTY_TITLE = 'No one is being notified yet'
export const EMPTY_BODY = 'Leads are still captured and scored. Nothing is lost while this is empty.'

/** The chooser's footnote under "Send it somewhere", verbatim. */
const DISPATCH_NOTE =
  'Credentials for these are held at the workspace level and only the owner can change them.'

/**
 * DECISION-016, said out loud where an operator would otherwise wonder.
 *
 * The dispatch column has no entries at M1 and the column still renders, which
 * is RULE-031 as a STRUCTURE: "never conflate them into a single 'send lead'
 * setting". A column that disappeared when it was empty would teach exactly the
 * conflation the rule forbids — that there is one kind of action, and it is
 * email.
 */
const DISPATCH_EMPTY = 'Sending the record somewhere arrives with the integrations.'

/* ── marks ──────────────────────────────────────────────────────────────── */

/**
 * The mockup's three glyphs, and the tone each square is drawn in: brand for a
 * notification, slate for a dispatch, nurture for the voice offer that is not
 * live yet. RULE-002 keeps red out of it — none of these is an error.
 */
const MARK: Record<ActionKind, { glyph: string; tone: string }> = {
  email: { glyph: '✉', tone: 'bg-mdx-brand text-mdx-white' },
  slack: { glyph: '#', tone: 'bg-mdx-brand text-mdx-white' },
  voice: { glyph: '☎', tone: 'bg-mdx-nurture text-mdx-white' },
  webhook: { glyph: '↗', tone: 'bg-mdx-slate text-mdx-white' },
  ghl: { glyph: '↗', tone: 'bg-mdx-slate text-mdx-white' },
}

/** RULE-002: nurture → review → hot is grey → amber → green, and no red. */
const PILL_TONE: Record<BandTone, 'hot' | 'review' | 'neutral'> = {
  hot: 'hot',
  review: 'review',
  nurture: 'neutral',
}

/** What the Results tab already counts, reused rather than counted again. */
type BandCount = { id: string; leads: number }

export function Routing({ funnelId }: { funnelId: string }) {
  const bands = useEditor((s) => s.funnel?.rubric.bands)
  const routing = useEditor((s) => s.funnel?.routing)

  /**
   * Highest band first — the mockup reads Hot, Review, Nurture.
   *
   * The Results tab sorts the other way ("smallest score first, so the eye
   * lands on the qualified row last"). Both are deliberate and they are
   * different jobs: that screen is a distribution to read, this one is a list
   * of decisions to make, and the decision that matters most is what happens to
   * the best leads.
   */
  const ladder = useMemo(
    () => [...(bands ?? [])].sort((a, b) => b.min - a.min),
    [bands],
  )

  const counts = useBandCounts(funnelId)

  if (!bands || !routing) return null

  return (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8 pt-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">Routing</span>
        <span className="text-mdx-13 text-mdx-slate">{ROUTING_ASIDE}</span>
      </div>

      <div className="flex flex-col gap-mdx-12">
        {ladder.map((band) => (
          <BandCard
            key={band.id}
            band={band}
            actions={routing.find((rule) => rule.band_id === band.id)?.actions ?? []}
            leads={counts?.find((row) => row.id === band.id)?.leads ?? null}
          />
        ))}
      </div>
    </>
  )
}

/* ── one band ───────────────────────────────────────────────────────────── */

function BandCard({
  band,
  actions,
  leads,
}: {
  band: Band
  actions: RoutingAction[]
  leads: number | null
}) {
  const [choosing, setChoosing] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const add = (kind: ActionKind) => {
    const id = newActionId()
    // The vocabulary's `add_action` (Story 27.2): `newRoutingAction`'s action —
    // instant, to nobody — on the band's rule, made for it if it has none yet.
    edit(`Add a ${ACTION_CATALOGUE[kind].label} action`, (draft) => addActionTo(draft, band.id, id, kind))
    setChoosing(false)
    // Straight into its own editor: an action with no recipients does nothing,
    // and the row that names that is the row you were about to open anyway.
    setEditingId(id)
  }

  return (
    // Named, so the card is addressable as a region rather than as the third
    // repetition of "When a lead is" — three cards on a screen and every one of
    // them says the same four words.
    <section
      aria-label={`When a lead is ${band.label}`}
      className="flex flex-col gap-mdx-16 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card"
    >
      <div className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-10">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">When a lead is</span>
        <Pill tone={PILL_TONE[band.tone]} dot>
          {band.label}
        </Pill>
        {leads === null ? null : (
          <span className="ml-auto shrink-0 text-mdx-13 tabular-nums text-mdx-slate">
            {leads === 1 ? '1 lead' : `${leads} leads`} in the last 30 days
          </span>
        )}
      </div>

      {actions.length === 0 ? (
        <div className="flex flex-col gap-mdx-6">
          <span className="text-mdx-15 font-mdx-semibold text-mdx-ink">{EMPTY_TITLE}</span>
          <span className="max-w-mdx-measure-52 text-mdx-14 text-mdx-slate">{EMPTY_BODY}</span>
        </div>
      ) : (
        <div className="flex flex-col gap-mdx-8">
          {actions.map((action) => (
            <ActionRow
              key={action.id}
              band={band}
              action={action}
              editing={editingId === action.id}
              onEdit={() => setEditingId(editingId === action.id ? null : action.id)}
              onDone={() => setEditingId(null)}
            />
          ))}
        </div>
      )}

      {choosing ? (
        <Chooser bandLabel={band.label} onPick={add} onCancel={() => setChoosing(false)} />
      ) : (
        <Button
          size="small"
          variant="ghost"
          className="self-start"
          onClick={() => setChoosing(true)}
        >
          Add an action
        </Button>
      )}
    </section>
  )
}

/* ── one action ─────────────────────────────────────────────────────────── */

/**
 * The row, and its editor beneath it.
 *
 * The subtitle leads with the SYSTEM — "Notification · …", "Dispatch · …" —
 * because RULE-031's two systems are only kept apart if each row says which one
 * it is. The chip on the right is the cadence, and it is on the ACTION: RULE-033,
 * "not of the recipient and not of the funnel".
 */
function ActionRow({
  band,
  action,
  editing,
  onEdit,
  onDone,
}: {
  band: Band
  action: RoutingAction
  editing: boolean
  onEdit: () => void
  onDone: () => void
}) {
  const entry = ACTION_CATALOGUE[action.kind]
  const mark = MARK[action.kind]

  return (
    <div className="flex flex-col gap-mdx-12 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12">
      <div className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-12">
        <span
          aria-hidden="true"
          className={`flex size-mdx-evidence-icon flex-none items-center justify-center rounded-mdx-control-sm text-mdx-13 font-mdx-bold ${mark.tone}`}
        >
          {mark.glyph}
        </span>
        <span className="flex min-w-0 flex-1 basis-mdx-row-identity flex-col">
          <span className="text-mdx-15 font-mdx-semibold text-mdx-ink">
            {describeAction(action)}
          </span>
          <span className="text-mdx-13 text-mdx-slate">
            {SYSTEM_LABEL[entry.system]} · {subtitleFor(action, band.label)}
          </span>
        </span>
        <Pill tone="brand" className="shrink-0">
          {cadenceLabel(action)}
        </Pill>
        <Button size="small" variant="secondary" className="shrink-0" onClick={onEdit}>
          {editing ? 'Done' : 'Edit'}
        </Button>
      </div>

      {editing ? <ActionEditor band={band} action={action} onDone={onDone} /> : null}
    </div>
  )
}

/**
 * The second half of the row's own sentence, from the mockup's four rows.
 *
 * "Notification · the subject line is the verdict" for the instant email —
 * Story 9.3's promise about the mail and PRD §1.9's rule about every headline.
 * "Notification · one message, every nurture lead from the week" for the
 * digest, because what an operator has to picture about a digest is what one
 * message HOLDS, and the band and the period are the two things that decide it.
 *
 * The band's own label, verbatim. The mockup's reads lowercase because the band
 * there is literally called Nurture; lowercasing an operator's word would turn
 * "Tier A" into "tier a", and the label is theirs (`services/funnels/bands.ts`).
 */
const PERIOD: Record<Exclude<Cadence, 'instant'>, string> = {
  hourly: 'hour',
  daily: 'day',
  weekly: 'week',
}

function subtitleFor(action: RoutingAction, bandLabel: string): string {
  if (ACTION_CATALOGUE[action.kind].system !== 'notification') {
    return ACTION_CATALOGUE[action.kind].blurb
  }
  if (action.cadence === 'instant') return 'the subject line is the verdict'
  return `one message, every ${bandLabel} lead from the ${PERIOD[action.cadence]}`
}

/* ── the editor ─────────────────────────────────────────────────────────── */

/**
 * Recipients and cadence, on the action.
 *
 * A FREE-TEXT LIST, and deliberately not the member picker DECISION-023 chose
 * for "Email it to a developer". PRD §4.7 permits three kinds of internal
 * recipient — "account owners, invited members, and **configured
 * lead-notification recipients**" — and a shared `sales@` inbox is the third
 * kind and is not a member of anything. `services/notify/recipients.ts` reads
 * this very list as the authority for that clause, so what is typed here is
 * what makes an address internal. The rule that a respondent's address can
 * never appear is enforced there, at the send, and it is enforced for the
 * digest and the export alike (RULE-067).
 */
function ActionEditor({
  band,
  action,
  onDone,
}: {
  band: Band
  action: RoutingAction
  onDone: () => void
}) {
  const write = (label: string, change: (target: RoutingAction) => void) => {
    edit(
      label,
      (draft) => {
        const target = draft.routing
          .find((rule) => rule.band_id === band.id)
          ?.actions.find((candidate) => candidate.id === action.id)
        if (target) change(target)
      },
      { coalesce: label.startsWith('Recipients') },
    )
  }

  /**
   * REPLACED, not patched. `withCadence` decides which companion fields survive
   * the change — including which ones must go — because `funnelSchema` refuses
   * a funnel where cadence and `cadence_at`/`cadence_day` disagree, and the
   * autosave parses the whole draft. Assigning over the old action would leave
   * a stale key behind, which is the half a patch cannot express.
   */
  const setCadence = (next: Cadence) =>
    edit('Change a cadence', (draft) => {
      const rule = draft.routing.find((entry) => entry.band_id === band.id)
      const index = rule?.actions.findIndex((candidate) => candidate.id === action.id) ?? -1
      if (!rule || index < 0) return
      rule.actions[index] = withCadence(rule.actions[index]!, next)
    })

  const timed = action.cadence === 'daily' || action.cadence === 'weekly'

  return (
    <div className="flex flex-col gap-mdx-12 border-t border-mdx-line-soft pt-mdx-12">
      <label className="flex flex-col gap-mdx-6">
        <span className="text-mdx-14 font-mdx-semibold text-mdx-ink">Recipients</span>
        <input
          type="text"
          value={action.recipients.join(', ')}
          placeholder="alice@example.com, sales@example.com"
          onChange={(event) =>
            write('Recipients', (target) => {
              target.recipients = event.target.value
                .split(',')
                .map((address) => address.trim())
                .filter(Boolean)
            })
          }
          className="h-mdx-input w-full min-w-0 rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 py-mdx-10 text-mdx-15 text-mdx-ink outline-none focus:border-mdx-brand"
        />
        <span className="text-mdx-13 text-mdx-slate">
          People in this organisation, and inboxes you name here. A respondent is never sent one of
          these.
        </span>
      </label>

      <div className="flex flex-wrap items-end gap-mdx-12">
        <select
          aria-label="How often"
          value={action.cadence}
          onChange={(event) => setCadence(event.target.value as Cadence)}
          className="h-mdx-input min-w-0 flex-1 basis-mdx-row-control-sm cursor-pointer rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 text-mdx-15 text-mdx-ink outline-none focus:border-mdx-brand"
        >
          {CADENCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {action.cadence === 'weekly' ? (
          <select
            aria-label="Which day"
            value={action.cadence_day ?? DEFAULT_CADENCE_DAY}
            onChange={(event) =>
              write('Change a cadence', (target) => {
                target.cadence_day = Number(event.target.value)
              })
            }
            className="h-mdx-input min-w-0 flex-1 basis-mdx-row-control-sm cursor-pointer rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 text-mdx-15 text-mdx-ink outline-none focus:border-mdx-brand"
          >
            {WEEKDAYS.map((day, index) => (
              <option key={day} value={index}>
                {day}s
              </option>
            ))}
          </select>
        ) : null}

        {timed ? (
          <input
            type="time"
            aria-label="At what time"
            value={action.cadence_at ?? DEFAULT_CADENCE_AT}
            onChange={(event) =>
              write('Change a cadence', (target) => {
                target.cadence_at = event.target.value
              })
            }
            className="h-mdx-input min-w-0 flex-1 basis-mdx-row-control-sm rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-14 text-mdx-15 text-mdx-ink outline-none focus:border-mdx-brand"
          />
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-mdx-12">
        <span className="text-mdx-13 text-mdx-slate">
          {timed
            ? 'Sent in the funnel’s own timezone, not the server’s.'
            : 'Sent as soon as the lead is scored.'}
        </span>
        {/* Remove, and nothing else. The row's own Edit button is what closes
            this — a second Done inside the panel would be two controls with one
            job and the same label, which is how a screenshot pass finds you. */}
        <Button
          size="small"
          variant="danger-outline"
          onClick={() => {
            edit('Remove an action', (draft) => {
              const rule = draft.routing.find((entry) => entry.band_id === band.id)
              if (!rule) return
              rule.actions = rule.actions.filter((candidate) => candidate.id !== action.id)
            })
            onDone()
          }}
        >
          Remove
        </Button>
      </div>
    </div>
  )
}

/* ── the chooser ────────────────────────────────────────────────────────── */

/**
 * Two groups, and they stay two.
 *
 * RULE-031 is a claim about STRUCTURE, so this renders both columns whatever is
 * in them. At M1 the second one is empty and says so, because a column that
 * vanished would leave a screen on which "an action" means "an email" — the
 * single "send lead" setting the rule forbids, arrived at by omission.
 *
 * DECISION-016 decides what is IN them: later-milestone features "do not render
 * at M1". Slack, Webhook and GoHighLevel are therefore absent rather than
 * greyed — there is no control to press. The voice offer is the exception the
 * story names, disabled with its milestone on it, exactly as PRD §7.5 already
 * sanctions for the Voice tab.
 */
function Chooser({
  bandLabel,
  onPick,
  onCancel,
}: {
  bandLabel: string
  onPick: (kind: ActionKind) => void
  onCancel: () => void
}) {
  return (
    <div className="flex flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24">
      <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">Add an action</span>
        <span className="text-mdx-13 text-mdx-slate">when a lead is {bandLabel}</span>
      </div>

      <div className="grid grid-cols-1 gap-mdx-20 sm:grid-cols-2">
        {ACTION_GROUPS.map((group) => {
          const kinds = kindsInGroup(group.id).filter(
            (kind) => ACTION_CATALOGUE[kind].offer !== 'hidden',
          )
          return (
            <div key={group.id} className="flex min-w-0 flex-col gap-mdx-8">
              <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
                {group.label}
              </span>
              {kinds.map((kind) => (
                <ChooserEntry key={kind} kind={kind} onPick={onPick} />
              ))}
              {/* The export's footnote says "credentials for THESE", so it
                  belongs to entries. With none, it would name nothing on the
                  screen — a sentence about controls that are not there. */}
              {kinds.length === 0 ? (
                <p className="m-0 text-mdx-13 text-mdx-slate">{DISPATCH_EMPTY}</p>
              ) : group.id === 'send' ? (
                <p className="m-0 text-mdx-13 text-mdx-slate">{DISPATCH_NOTE}</p>
              ) : null}
            </div>
          )
        })}
      </div>

      <Button size="small" variant="secondary" className="self-start" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  )
}

function ChooserEntry({ kind, onPick }: { kind: ActionKind; onPick: (kind: ActionKind) => void }) {
  const entry = ACTION_CATALOGUE[kind]
  const mark = MARK[kind]

  const body = (
    <>
      <span
        aria-hidden="true"
        className={`flex size-mdx-evidence-icon flex-none items-center justify-center rounded-mdx-control-sm text-mdx-13 font-mdx-bold ${mark.tone}`}
      >
        {mark.glyph}
      </span>
      <span className="flex min-w-0 flex-1 basis-mdx-row-identity-sm flex-col">
        <span className="text-mdx-15 font-mdx-semibold text-mdx-ink">{entry.label}</span>
        <span className="text-mdx-13 text-mdx-slate">{entry.blurb}</span>
      </span>
    </>
  )

  // Wraps rather than squeezes. Two columns inside the reading measure leave a
  // chooser entry about 275px wide, and a `Coming soon` badge holding its place
  // beside the blurb took it down to three lines — found by the 1440 pass, and
  // the same 130/170 basis pair `design/tokens.css` names for a modal row.
  const box =
    'flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-8 rounded-mdx-control ' +
    'border border-mdx-line px-mdx-14 py-mdx-12 text-left'

  if (entry.offer === 'coming-soon') {
    return (
      <span className={`${box} cursor-not-allowed opacity-mdx-recessed`}>
        {body}
        <span className="shrink-0 rounded-mdx-pill bg-mdx-nurture-tint px-mdx-9 py-mdx-3 text-mdx-12 font-mdx-semibold text-mdx-slate">
          Coming soon
        </span>
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={() => onPick(kind)}
      className={`${box} cursor-pointer bg-transparent transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground`}
    >
      {body}
    </button>
  )
}

/* ── the 30-day counts ──────────────────────────────────────────────────── */

/**
 * "61 leads in the last 30 days", from the rollup the Results tab already
 * computes.
 *
 * A SECOND QUERY WOULD BE A SECOND ANSWER. `services/funnels/results.ts` counts
 * leads per band over a window and excludes over-cap rows from the split
 * (DECISION-061); a count written here would be a different number on two tabs
 * of the same funnel, and the one that was wrong would be whichever was read
 * second.
 *
 * A failure is silent on purpose. The count is context for a decision, not the
 * decision — routing must stay editable when the rollup is unavailable, and an
 * error banner over a working screen would be RULE-078's failure needing a
 * human, for a number.
 */
function useBandCounts(funnelId: string): BandCount[] | null {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null
  const [counts, setCounts] = useState<BandCount[] | null>(null)

  const load = useCallback(async () => {
    if (!workspaceId) return
    try {
      const rollup = await apiGet<{ bands: BandCount[] }>(
        `/v1/w/${workspaceId}/funnels/${funnelId}/results?range=30d`,
      )
      setCounts(rollup.bands)
    } catch {
      setCounts(null)
    }
  }, [workspaceId, funnelId])

  useEffect(() => {
    void load()
  }, [load])

  return counts
}
